import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileText, MessageSquare } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import api from "../lib/api";
import { categoryLabel } from "../lib/categories";

export default function SearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ articles: [], opinions: [] });
  const [searched, setSearched] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults({ articles: [], opinions: [] });
      setSearched(false);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults({ articles: [], opinions: [] });
      setSearched(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
        setSearched(true);
      } catch (e) { /* ignore */ }
    }, 280);
    return () => clearTimeout(t);
  }, [q]);

  const empty = searched && results.articles.length === 0 && results.opinions.length === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="site-search-modal" className="bg-surface border-white/10 text-porcelain sm:max-w-2xl p-0 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <Search size={18} className="text-kinetic shrink-0" />
          <input
            ref={inputRef}
            data-testid="search-query-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search stories, voices, topics…"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-ash/60"
          />
          <kbd className="text-[10px] font-mono text-ash border border-white/10 rounded px-2 py-1">ESC</kbd>
        </div>

        <div data-testid="search-results-list" className="max-h-[55vh] overflow-y-auto">
          {empty && (
            <p className="text-ash text-sm font-mono px-5 py-10 text-center">Nothing found for "{q}" — try another word.</p>
          )}
          {results.articles.length > 0 && (
            <div className="py-3">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-kinetic px-5 pb-2">Stories</p>
              {results.articles.map((a) => (
                <Link key={a.id} to={`/article/${a.slug}`} onClick={onClose} data-testid={`search-result-article-${a.slug}`}
                  className="flex items-start gap-3 px-5 py-3 hover:bg-white/5 transition-colors">
                  <FileText size={15} className="text-ash mt-1 shrink-0" />
                  <div>
                    <p className="text-sm font-medium leading-snug">{a.title}</p>
                    <p className="text-[11px] font-mono text-ash mt-1 uppercase tracking-[0.15em]">{categoryLabel(a.category)} · {a.read_minutes} min</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {results.opinions.length > 0 && (
            <div className="py-3 border-t border-white/5">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-crimson px-5 pb-2">Community Voices</p>
              {results.opinions.map((o) => (
                <Link key={o.id} to="/voices" onClick={onClose} data-testid={`search-result-opinion-${o.id}`}
                  className="flex items-start gap-3 px-5 py-3 hover:bg-white/5 transition-colors">
                  <MessageSquare size={15} className="text-ash mt-1 shrink-0" />
                  <div>
                    <p className="text-sm font-medium leading-snug">{o.title}</p>
                    <p className="text-[11px] font-mono text-ash mt-1 uppercase tracking-[0.15em]">@{o.author_name} · {o.upvotes} votes</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {!q.trim() && (
            <p className="text-ash/60 text-xs font-mono px-5 py-8 text-center uppercase tracking-[0.2em]">
              Start typing — results appear as you go
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
